import { EventEmitter } from 'eventemitter3';

export type EventMap = Record<string, unknown>;

export class ChannelEventBus<TEvents extends EventMap = EventMap> {
  private emitter: EventEmitter;
  private readonly channelId: string;

  /**
   * Create a new channel event bus
   * @param channelId - The channel ID for this event bus
   */
  constructor(channelId: string) {
    this.emitter = new EventEmitter();
    this.channelId = channelId;

    this.getChannelId = this.getChannelId.bind(this);
    this.publish = this.publish.bind(this);
    this.subscribe = this.subscribe.bind(this);
    this.subscribeOnce = this.subscribeOnce.bind(this);
    this.hasListeners = this.hasListeners.bind(this);
    this.removeAllListeners = this.removeAllListeners.bind(this);
  }

  /**
   * Get the channel ID for this event bus
   */
  public getChannelId(): string {
    return this.channelId;
  }

  /**
   * Publish an event on this channel
   * @param eventName - The name of the event
   * @param payload - The event payload
   */
  public publish<K extends keyof TEvents>(eventName: K, payload: TEvents[K]): void {
    // Format: "channelId:eventName"
    const channelEvent = `${this.channelId}:${String(eventName)}`;
    this.emitter.emit(channelEvent, payload);
  }

  /**
   * Subscribe to an event on this channel
   * @param eventName - The name of the event to subscribe to
   * @param listener - The callback function
   * @returns A function to unsubscribe
   */
  public subscribe<K extends keyof TEvents>(
    eventName: K,
    listener: (payload: TEvents[K]) => void,
  ): () => void {
    const channelEvent = `${this.channelId}:${String(eventName)}`;

    // Add the listener
    this.emitter.on(channelEvent, listener);

    // Return an unsubscribe function
    return () => {
      this.emitter.off(channelEvent, listener);
    };
  }

  /**
   * Subscribe to an event once on this channel
   * @param eventName - The name of the event to subscribe to
   * @param listener - The callback function
   */
  public subscribeOnce<K extends keyof TEvents>(
    eventName: K,
    listener: (payload: TEvents[K]) => void,
  ): void {
    const channelEvent = `${this.channelId}:${String(eventName)}`;
    this.emitter.once(channelEvent, listener);
  }

  /**
   * Check if this channel has listeners for a specific event
   * @param eventName - The event name
   * @returns Whether the event has listeners on the channel
   */
  public hasListeners(eventName: keyof TEvents): boolean {
    const channelEvent = `${this.channelId}:${String(eventName)}`;
    return this.emitter.listeners(channelEvent).length > 0;
  }

  /**
   * Remove all listeners for a specific event on this channel
   * @param eventName - The event name (optional - if not provided, all events on this channel are cleared)
   */
  public removeAllListeners(eventName?: keyof TEvents): void {
    if (eventName) {
      const channelEvent = `${this.channelId}:${String(eventName)}`;
      this.emitter.removeAllListeners(channelEvent);
    } else {
      // Get all events that match the channel prefix
      // This works because EventEmitter3 provides a means to get all event names
      const allEvents = Object.keys(this.emitter.eventNames());
      const channelEvents = allEvents.filter((event) => event.startsWith(`${this.channelId}:`));

      // Remove all matching events
      channelEvents.forEach((event) => {
        this.emitter.removeAllListeners(event);
      });
    }
  }
}

// Registry to keep track of all created event buses
// eslint-disable-next-line @typescript-eslint/no-extraneous-class -- this is a utility class
export class EventBusRegistry {
  private static registry = new Map<string, ChannelEventBus>();

  /**
   * Get or create an event bus for a specific channel
   * @param channelId - The channel identifier
   * @returns The existing or newly created event bus
   */
  public static getOrCreate<TEvents extends EventMap>(channelId: string): ChannelEventBus<TEvents> {
    // Check if we already have this channel in our registry
    if (EventBusRegistry.registry.has(channelId)) {
      return EventBusRegistry.registry.get(channelId) as ChannelEventBus<TEvents>;
    }

    // Create a new event bus if it doesn't exist
    const eventBus = new ChannelEventBus<TEvents>(channelId);
    EventBusRegistry.registry.set(channelId, eventBus);
    return eventBus;
  }

  /**
   * Check if an event bus exists for a specific channel
   * @param channelId - The channel identifier
   * @returns Whether the event bus exists
   */
  public static exists(channelId: string): boolean {
    return EventBusRegistry.registry.has(channelId);
  }

  /**
   * Remove an event bus from the registry
   * @param channelId - The channel identifier
   * @returns Whether the event bus was removed
   */
  public static remove(channelId: string): boolean {
    if (EventBusRegistry.registry.has(channelId)) {
      const eventBus = EventBusRegistry.registry.get(channelId);
      if (eventBus) eventBus.removeAllListeners();
      return EventBusRegistry.registry.delete(channelId);
    }
    return false;
  }

  /**
   * Get all registered channel IDs
   * @returns Array of registered channel IDs
   */
  public static getChannelIds(): string[] {
    return Array.from(EventBusRegistry.registry.keys());
  }

  /**
   * Clear all event buses from the registry
   */
  public static clear(): void {
    EventBusRegistry.registry.forEach((eventBus) => {
      eventBus.removeAllListeners();
    });
    EventBusRegistry.registry.clear();
  }
}

export function createEventBus<TEvents extends EventMap>(
  channelId: string,
): ChannelEventBus<TEvents> {
  return EventBusRegistry.getOrCreate<TEvents>(channelId);
}
